<template>
  <div
    class="requirement cursor-pointer rounded-xl border-2 border-dashed border-transparent p-4 active:border-transparent"
    :class="{ selected: userHasSelected }"
  >
    <div v-if="!isButton">
      <input :id="value" type="checkbox" class="sr-only" :value="value" :checked="userHasSelected" @change="updateSelections" />
      <label :for="value" class="flex flex-col items-center centered cursor-pointer" @click="setClicked(true)">
        <div aria-hidden="true" class="requirement-graphic">
          <component :is="resolvedIcon" :class="iconClasses" role="img" v-bind="iconBindings" />
        </div>
        <span class="requirement-label text-subtitle my-8 cursor-pointer">
          <slot />
        </span>
      </label>
    </div>
    <button
      v-else
      :id="value"
      :value="value"
      type="button"
      class="outline-none cursor-pointer flex flex-col items-center centered m-auto"
      @click="buttonClicked"
      @mousedown="setClicked(true)"
      @mouseup="setClicked(true)"
    >
      <div aria-hidden="true" class="requirement-graphic">
        <component :is="resolvedIcon" :class="iconClasses" role="img" v-bind="iconBindings" />
      </div>
      <p class="requirement-label text-subtitle my-8 cursor-pointer">
        <slot />
      </p>
    </button>
  </div>
</template>

<script lang="ts">
import { markComponentRaw } from '@/helpers/markComponentRaw';

export default {
  name: 'Requirement',
  props: {
    isButton: {
      type: Boolean,
      default: false,
    },
    value: {
      type: String,
      required: true,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    requirementsSelections: {
      type: Array,
      default: () => [],
    },
    icon: {
      type: Object,
      required: true,
    },
    iconSize: {
      type: Number,
      default: 40,
    },
    fillsCircle: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['click', 'update:requirementsSelections'],
  data() {
    return {
      wasClicked: false,
    };
  },
  computed: {
    userHasSelected() {
      return this.selected || this.requirementsSelections.includes(this.value);
    },
    resolvedIcon() {
      return markComponentRaw(this.icon);
    },
    iconClasses(): string[] {
      return this.fillsCircle ? ['fills-circle'] : [];
    },
    iconBindings(): { color: string; width?: number; height?: number } {
      if (this.fillsCircle) {
        return { color: 'currentColor' };
      }

      return {
        color: 'currentColor',
        width: this.iconSize,
        height: this.iconSize,
      };
    },
  },
  methods: {
    buttonClicked() {
      this.$emit('click');
    },
    setClicked(clicked) {
      this.wasClicked = clicked;
    },
    updateSelections(event) {
      const isChecked = event.target.checked;
      const newSelections = [...this.requirementsSelections];
      if (isChecked) {
        newSelections.push(this.value);
      } else {
        newSelections.splice(newSelections.indexOf(this.value), 1);
      }
      this.$emit('update:requirementsSelections', newSelections);

      if (this.wasClicked) {
        this.removeMouseFocus();
      }
    },
    removeMouseFocus() {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      this.wasClicked = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.requirement {
  color: theme('colors.grey.text.subdued');
}

.requirement-graphic {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 4rem;
  height: 4rem;
  box-sizing: border-box;
  border: 1px solid currentColor;
  border-radius: 50%;
  background-color: theme('colors.foreground');
  color: theme('colors.grey.graphics.subdued');

  :deep(svg.fills-circle) {
    width: 100%;
    height: 100%;
  }
}

.requirement-label {
  color: theme('colors.grey.text.subdued');
}

@media (hover: hover) {
  .requirement:not(.selected):hover {
    color: theme('colors.brand.contrast');

    .requirement-graphic {
      color: theme('colors.brand.contrast');
    }

    .requirement-label {
      color: theme('colors.brand.contrast');
    }
  }
}

.requirement:not(.selected):focus-within:has(:focus-visible) {
  border-color: theme('colors.brand.contrast');
  color: theme('colors.brand.contrast');

  .requirement-graphic {
    color: theme('colors.brand.contrast');
  }

  .requirement-label {
    color: theme('colors.brand.contrast');
  }
}

.requirement.selected {
  color: theme('colors.primary.DEFAULT');

  .requirement-graphic {
    border-width: 2px;
    background-color: theme('colors.primary.subdued');
    color: theme('colors.primary.DEFAULT');
  }
}

.requirement.selected .requirement-label {
  color: theme('colors.primary.DEFAULT');
}
</style>
